// Services/ApiService.js
import { API_CONFIG } from '../Utils/APIConfig';
import { getAuthData } from '../Utils/AuthUtils';
import { getLastSyncDT, setLastSyncDT } from '../Utils/APIConfig';
import { getActiveData, getAgencyData, getUserData, setStorageData, setUserConfigsData, setUserData, getStorageData, setSCRMConfigs } from '../Utils/Storage';
import { setInIndexedDB, updateIndexedDB, removeFromIndexedDB, clearIndexedDB, getFromIndexedDB, clearAgencyDataInIndexedDB } from '../Utils/IndexedDBUtils';
import { toast } from 'sonner';
import store from '../Store/Store';
import { setGlobalLoading } from '../Store/Actions';
import { CreditCard } from 'lucide-react';

class APIService {
  constructor() {
    this.isLive = false;
    this.apiEndpoints = {
      set: `${API_CONFIG.BASE_URL}/Set.asmx/SendChange`,
      sendForm: `${API_CONFIG.BASE_URL}/Set.asmx/SendChangeForm`, // Added
      get: `${API_CONFIG.BASE_URL}/Fetch.asmx/GetData`,
      systemChange: `${API_CONFIG.BASE_URL}/Set.asmx/SendSystemChange`,
      access: `${API_CONFIG.BASE_URL}/Access.asmx/SendChange`,
      accessGet: `${API_CONFIG.BASE_URL}/Access.asmx/GetData`
    };
    this.dispatchCallback = null;
    this.agencyContext = null;
  }

  // Helper: Set Redux dispatch callback
  setDispatchCallback(dispatchFn) {
    this.dispatchCallback = dispatchFn;
  }

  // Helper: Check if API is called from Web or Mobile
  apiCheckWeb() {
    try {
      const jarvisCookie = localStorage.getItem('Jarvis');
      if (jarvisCookie) {
        const parsedCookie = JSON.parse(jarvisCookie);
        if (parsedCookie.length > 0) {
          return parsedCookie[0].IsWeb != null ? parsedCookie[0].IsWeb : true;
        }
      }
      return true;
    } catch (error) {
      console.error('Error checking web status:', error);
      return true;
    }
  }

  // Helper: Handle API fetch request
  async fetchAPI(endpoint, body) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      body: JSON.stringify({ parameters: body })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (!result?.d) {
      throw new Error('Invalid response format');
    }

    return result.d;
  }

  async homeResume(isWithIDB = "1") {
    try {
      const authData = getAuthData();
      const currentUserID = authData?.UserID ? String(authData.UserID) : "0";
      const forAgencyID = authData.AgencyID;
      // mimic DataTable from C# (with isWithIDB column)
      const homeResumeParams = [
        { isWithIDB: isWithIDB || "1" }
      ];

      const preparedHomeResumeData = {
        changeType: "HOMERESUME",   // marker like OCNTCADD
        recUID: currentUserID,
        forAgencyID: forAgencyID,
        data: JSON.stringify(homeResumeParams)
      };

      const options = isWithIDB === "0" ? { lastSyncDT: null, isFromSCReset: true, isWithH: 0 } : {};

      // Case 0 → Home Page (as per C# switch)
      const result = await this.sendChange(0, "action", preparedHomeResumeData, options);
      return result;
    } catch (error) {
      this.handleAPIError(error, "Home Resume");
    }
  }

  async setInitData() {
    try {
      const authData = getAuthData();
      const forAgencyID = authData.AgencyID;
      // mimic DataTable from C# (with isWithIDB column)

      const preparedHomeResumeData = {
        forAgencyID: forAgencyID,
        changeType: "INIT",
      };

      // Case 0 → Home Page (as per C# switch)
      return
      const result = await this.sendChange(12, "initData", preparedHomeResumeData);
      return result;
    } catch (error) {
      this.handleAPIError(error, "Home Resume");
    }
  }

  // Helper: Handle error display and throw
  handleAPIError(error, context) {
    console.error(`${context} API Error:`, error);
    const message = error.message || 'Something went wrong while processing your request. Please contact Team Biziverse. E-001';
    throw error;
  }

  // Helper: Process table data for storage
  async processTableData(tblType, tableData, localStorageData) {
    for (const item of tableData) {
      const existingIndex = localStorageData.findIndex(x => x.NodeID === item.NodeID);
      if (item.IsRemove === 1 || item.IsRemove === true) {
        if (existingIndex > -1) localStorageData.splice(existingIndex, 1);
      } else {
        if (existingIndex > -1) localStorageData[existingIndex] = item;
        else localStorageData.push(item);
      }
    }
    return localStorageData;
  }

  setAgencyContext(agencyID) {
    if (!agencyID) {
      this.agencyContext = null;
      return;
    }
    try {
      const myAgencies = getStorageData('MyAgencies') || [];
      const matchingAgency = myAgencies.find(agn => String(agn.AgencyID) === String(agencyID));
      if (matchingAgency) {
        this.agencyContext = {
          userID: matchingAgency.UserID,
          forAgencyID: matchingAgency.AgencyID
        };
      } else {
        this.agencyContext = null;
      }
    } catch (e) {
      console.error('Error setting agency context in APIService:', e);
      this.agencyContext = null;
    }
  }

  clearAgencyContext() {
    this.agencyContext = null;
  }

  prepareAuthenticatedParameters(moduleID, actionType, data, options = {}) {
    const authData = getAuthData();
    if (!authData) {
      throw new Error('Authentication data not found');
    }

    const lastSyncDT = options.hasOwnProperty('lastSyncDT') ? options.lastSyncDT : getLastSyncDT();
    const isWeb = this.apiCheckWeb();

    const resolvedUserID = options.userID || (this.agencyContext && this.agencyContext.userID) || authData.UserID;
    const resolvedAgencyID = options.forAgencyID || (this.agencyContext && this.agencyContext.forAgencyID) || authData.AgencyID;

    // Automatically patch User ID fields inside data payload if resolvedUserID is different
    if (resolvedUserID !== authData.UserID && data) {
      try {
        const fieldsToPatch = ['pocUserID', 'recUID', 'uid', 'UID', 'userID', 'UserID', 'createdUID', 'CreatedUID'];
        if (Array.isArray(data)) {
          data = data.map(item => {
            const newItem = { ...item };
            fieldsToPatch.forEach(field => {
              if (newItem[field] === authData.UserID || String(newItem[field]) === String(authData.UserID)) {
                newItem[field] = resolvedUserID;
              }
            });
            return newItem;
          });
        } else if (typeof data === 'object') {
          data = { ...data };
          fieldsToPatch.forEach(field => {
            if (data[field] === authData.UserID || String(data[field]) === String(authData.UserID)) {
              data[field] = resolvedUserID;
            }
          });
        }
      } catch (err) {
        console.error('Error patching UserID fields in change data:', err);
      }
    }

    const finalParams = [{
      UserID: resolvedUserID,
      Token: isWeb ? authData.WebToken : authData.AppToken,
      ModuleID: moduleID,
      ActionType: actionType,
      ForAgencyID: resolvedAgencyID,
      LastSyncDT: lastSyncDT,
      IsWeb: isWeb,
      CC: authData.CC || 91,
      Mobile: authData.Mobile || '',
      Data: JSON.stringify(Array.isArray(data) ? data : [data])
    }];
    return finalParams;
  }

  prepareUnauthParams(moduleID, actionType, data) {
    return [{
      UserID: 0,
      Token: '',
      ForAgencyID: 0,
      LastSyncDT: '',
      IsWeb: true,
      ModuleID: moduleID,
      ActionType: actionType,
      Data: JSON.stringify(Array.isArray(data) ? data : [data])
    }];
  }
  // Update the unauthSendChange method to properly parse the response for unauth flows
  async unauthSendChange(moduleID, actionType, data, endpoint = this.apiEndpoints.access) {
    try {
      const parameters = this.prepareUnauthParams(moduleID, actionType, data);
      const fullResponse = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=UTF-8' },
        body: JSON.stringify({ parameters: JSON.stringify(parameters) })  // Match Angular body structure
      });

      if (!fullResponse.ok) {
        throw new Error(`HTTP error! status: ${fullResponse.status}`);
      }

      const result = await fullResponse.json();

      if (!result?.d) {
        throw new Error('Invalid response format');
      }

      const d = result.d;
      if (d.error === true) {
        throw new Error(d.message || 'API error');
      }

      // For checkIfExists/sendOTP, return parsed d.data
      if (d.data) {
        try {
          return JSON.parse(d.data);
        } catch (parseError) {
          console.error('Parse error:', parseError);
          throw new Error('Invalid response data');
        }
      }

      return d; // Fallback
    } catch (error) {
      console.error('Unauth API Error:', error);
      throw error;
    }
  }

  // Check access method - equivalent to Angular checkAccess()
  async checkAccess() {

    const authData = getAuthData();
    if (!authData) {
      throw new Error('Authentication data not found');
    }

    const lastSyncDT = getLastSyncDT();
    const isWeb = this.apiCheckWeb();

    const checkAccessData = {
      agencyID: authData.AgencyID,
      userID: authData.UserID,
      isWeb: isWeb,
      token: isWeb ? authData.WebToken : authData.AppToken,
      lastSyncDT: lastSyncDT
    };

    const parameters = [{
      ModuleID: 1,
      ActionType: "checkAccess",
      Data: JSON.stringify([checkAccessData])
    }];

    try {
      const result = await this.fetchAPI(this.apiEndpoints.access, JSON.stringify(parameters));

      if (result?.data) {
        const respData = JSON.parse(result.data);
        const errCode = respData?.Table?.[0]?.ErrCode;

        if (errCode === 1) {
          // Verification success
          return { success: true, shouldRedirect: true };
        } else {
          // Verification failed - reset everything
          this.resetEverything();
          return { success: false, shouldRedirect: false };
        }
      }

      return { success: true, shouldRedirect: true };
    } catch (error) {
      return { success: true, shouldRedirect: true };
    }

  }

  // Upload PDF file method
  async uploadFile(file, basePath = "Transactions", fileType = 'both') {
    const authData = getAuthData();
    if (!authData?.AgencyID) {
      return;
    }
    const agencyID = authData.AgencyID;
    // Validate file size (max 2MB) - moved from handleFileChange
    if (file.size > 2 * 1024 * 1024) {
      throw new Error("File size should be less than 2 MB.");
    }

    // Auto-detect file type if not specified or if 'both' is passed
    if (fileType === 'both') {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const imageExtensions = ['bmp', 'jpeg', 'jpg', 'png', 'gif', 'tiff', 'webp'];
      const pdfExtensions = ['pdf'];

      if (imageExtensions.includes(fileExtension)) {
        fileType = 'image';
      } else if (pdfExtensions.includes(fileExtension)) {
        fileType = 'pdf';
      } else {
        throw new Error('Unsupported file type');
      }
    }

    try {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const base64Data = reader.result;
            const fileName = file.name;

            const response = await fetch(`${API_CONFIG.BASE_URL}/ExtUtilities.asmx/UploadFile`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json;charset=UTF-8' },
              body: JSON.stringify({
                data: base64Data,
                basePath: basePath,
                agencyID: agencyID,
                fileName: fileName,
                fileType: fileType  // Now passes the detected type
              })
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            if (!result?.d) {
              throw new Error('Invalid response format');
            }

            const responseData = result.d;
            if (responseData.error) {
              throw new Error(responseData.message);
            }

            resolve(responseData);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
    } catch (error) {
      this.handleAPIError(error, 'File Upload');
    }
  }

  // Upload File via FormData
  async uploadFormFile(file, basePath = "Transactions", fileType = 'both') {
    const authData = getAuthData();
    if (!authData?.AgencyID) return;
    const agencyID = authData.AgencyID;

    if (file.size > 2 * 1024 * 1024) throw new Error("File size should be less than 2MB");

    if (fileType === 'both') {
      const ext = file.name.split('.').pop().toLowerCase();
      if (['bmp', 'jpeg', 'jpg', 'png', 'gif', 'tiff'].includes(ext)) fileType = 'image';
      else if (['pdf'].includes(ext)) fileType = 'pdf';
      else throw new Error('Unsupported file type');
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("agencyID", agencyID);
      formData.append("basePath", basePath);
      formData.append("fileType", fileType);

      const response = await fetch(`${API_CONFIG.BASE_URL}/ExtUtilities.asmx/UploadFileForm`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const responseData = result.d ? result.d : result; // Handle wrapped or raw response

      if (responseData.error) throw new Error(responseData.message);

      return responseData;
    } catch (error) {
      this.handleAPIError(error, 'File Upload Form');
    }
  }

  // Reset everything - clear storage and redirect
  resetEverything() {
    localStorage.clear();
    const request = indexedDB.deleteDatabase('SahiCRMIDB');
    request.onsuccess = () => { console.log("Deleted database successfully"); window.location.href = "/"; };
    request.onerror = () => { console.log("Couldn't delete database"); window.location.href = "/"; };
    request.onblocked = () => { console.log("Couldn't delete database due to the operation being blocked"); window.location.href = "/"; };
  }

  // Main API call method - equivalent to sendChange()
  async sendChange(moduleID, actionType, data, options = {}) {
    if (moduleID === 1 && (actionType === 'signUp' || actionType === 'login')) {
      return await this.unauthSendChange(moduleID, actionType, data, this.apiEndpoints.set);
    }

    const excludedLoadingActions = [
      { moduleID: 15, actionType: "action" },
      { moduleID: 14, actionType: "setStatus" },
      { moduleID: 6, actionType: "setProfile" },
    ];

    const showLoading = !excludedLoadingActions.some(
      (item) => item.moduleID === moduleID && item.actionType === actionType
    );

    if (showLoading) {
      store.dispatch(setGlobalLoading(true));
    }

    // Authenticated logic remains
    try {
      const parameters = this.prepareAuthenticatedParameters(moduleID, actionType, data, options);
      const result = await this.fetchAPI(this.apiEndpoints.set, JSON.stringify(parameters));
      await this.parseResponse(result, options.isFromSCReset, options.isWithH);
      return result;
    } catch (error) {
      this.handleAPIError(error, 'Send Change');
    } finally {
      if (showLoading) {
        store.dispatch(setGlobalLoading(false));
      }
    }
  }

  // Send Data via FormData (Multipart)
  async sendChangeForm(moduleID, actionType, data, options = {}) {
    const showLoading = true;
    showLoading && store.dispatch(setGlobalLoading(true));

    try {
      const parameters = this.prepareAuthenticatedParameters(moduleID, actionType, data, options);

      const formData = new FormData();
      // 'parameters' key must match C# Request.Form["parameters"]
      formData.append("parameters", JSON.stringify(parameters));

      const response = await fetch(this.apiEndpoints.sendForm, {
        method: 'POST',
        body: formData // Content-Type is set automatically by browser with boundary
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result?.d) {
        throw new Error('Invalid response format');
      }

      const responseData = result.d;
      await this.parseResponse(responseData, options.isFromSCReset, options.isWithH);
      return responseData;

    } catch (error) {
      this.handleAPIError(error, 'Send Change Form');
    } finally {
      showLoading && store.dispatch(setGlobalLoading(false));
    }
  }

  // Reset-specific API call - equivalent to sendChangeReset()
  async sendChangeReset(moduleID, actionType, data, options = {}) {
    // For reset calls, we want to override certain parameters
    const resetOptions = {
      ...options,
      isFromSCReset: true,
      lastSyncDT: null  // Force LastSyncDT to be null for reset calls
    };

    return this.sendChange(moduleID, actionType, data, resetOptions);
  }

  // System change API call - equivalent to sendSystemChange()
  async sendSystemChange(parameters) {
    store.dispatch(setGlobalLoading(true));

    try {
      const body = JSON.stringify(this.prepareAuthenticatedParameters(1, 'set', parameters));
      return await this.fetchAPI(this.apiEndpoints.systemChange, body);
    } catch (error) {
      this.handleAPIError(error, 'System Change');
    } finally {
      store.dispatch(setGlobalLoading(false));
    }
  }

  // Get data without update package - equivalent to getData()
  async getData(moduleID, actionType, data, options = {}) {
    try {
      const parameters = this.prepareAuthenticatedParameters(moduleID, actionType, data, options);
      const result = await this.fetchAPI(this.apiEndpoints.get, JSON.stringify(parameters));

      if (result.error === true) {
        alert(result.message);
        this.resetEverything();
        throw new Error(result.message);
      }

      const fetchDataSet = result.updatePackage ? JSON.parse(result.updatePackage) : null;
      if (!fetchDataSet?.Table?.[0]) {
        throw new Error('Invalid response received from the server.');
      }
      if (fetchDataSet.Table[0].FnErrCode === 1) {
        alert(fetchDataSet.Table[0].ErrMsg);
        this.resetEverything();
        throw new Error(fetchDataSet.Table[0].ErrMsg);
      }

      return result;
    } catch (error) {
      this.handleAPIError(error, 'Get Data');
    }
  }

  // Parse API response and update local storage - equivalent to parseResponse()
  async parseResponse(resObj, isFromSCReset = false, isWithH = false) {
    if (!resObj) return;

    if (resObj.error && resObj.message !== '') {
      toast.error(resObj.message);
    }

    if (resObj.packageType === 100) {
      this.resetEverything();
      return;
    }

    try {
      const dataSet = JSON.parse(resObj.updatePackage);

      const isEmpty = !dataSet ||
        (Array.isArray(dataSet) && dataSet.length === 0) ||
        (typeof dataSet === 'object' && Object.keys(dataSet).length === 0) ||
        !dataSet.Table ||
        (Array.isArray(dataSet.Table) && dataSet.Table.length === 0);

      // No usable data table with a server message means the response is status-only
      // Do not treat that as a session failure that wipes local data (including the
      // logged-in Jarvis) via resetEverything().
      if (isEmpty && resObj.message) {
        return resObj;
      }

      if (isEmpty || dataSet?.Table?.[0]?.AccessErrCode === 0) {
        this.resetEverything();
        return;
      }

      await this.setPackageInfo(dataSet, isFromSCReset, isWithH);
      return dataSet;
    } catch (error) {
      console.error('Error parsing response:', error);
      return resObj;
    }
  }

  // Set package information in storage - equivalent to setPackageinfo()
  async setPackageInfo(dataSet, isFromSCReset = false, isWithH = false) {
    ['Conns', 'Threads', 'Tasks', 'Contracts'].forEach(key => localStorage.removeItem(key));

    let dbAccess = dataSet.Table || [];
    if (dbAccess[0]?.NewLastSyncDT) {
      setLastSyncDT(dbAccess[0].NewLastSyncDT);
    }

    if (dbAccess.length > 0) {
      if (dbAccess[0].AccessErrCode == 10 || dbAccess[0].AccessErrCode === 1) {
        await this.updateBizLocalData(dataSet, isFromSCReset, isWithH, dbAccess[0].AccessErrCode);
        return;
      } else {
        const connsTbl = [];
        const connsRemoveTbl = [];
        const actsTbl = [];
        const actsRemoveTbl = [];
        const contractsTbl = []; // Add this line
        const contractsRemoveTbl = [];
        const taskTbl = [];
        const taskRemoveTbl = [];
        const dataSetKeys = Object.keys(dataSet);
        const tableCount = dataSetKeys.filter(key => key.startsWith('Table')).length;

        for (let i = 0; i < tableCount; i++) {
          const tableKey = i === 0 ? "Table" : `Table${i}`;
          const tableData = dataSet[tableKey];
          if (tableData && tableData.length > 0) {
            const tblType = tableData[0].Tbl;
            const filteredData = tableData.filter(item => item.IsRemove !== 1 && item.IsRemove !== true);
            const removedData = tableData.filter(item => item.IsRemove === 1 || item.IsRemove === true);
            if (tblType === 'Conns') { connsTbl.push(...filteredData); connsRemoveTbl.push(...removedData); }
            else if (tblType === 'Threads') { actsTbl.push(...filteredData); actsRemoveTbl.push(...removedData); }
            else if (tblType === 'Contracts') { contractsTbl.push(...filteredData); contractsRemoveTbl.push(...removedData); } // Add this line
            else if (tblType === 'Tasks') { taskTbl.push(...filteredData); taskRemoveTbl.push(...removedData); }
            else if (tblType === 'MyAgencies') {

              setStorageData('MyAgencies', filteredData.map(({ Tbl, ...rest }) => rest));
            }
            else if (tblType) setStorageData(tblType, filteredData);
          }
        }


        const shouldSkipIDBWrite = isFromSCReset && isWithH === 0;

        if (!shouldSkipIDBWrite) {
          if (connsRemoveTbl.length > 0) await removeFromIndexedDB('Conns', connsRemoveTbl);
          if (connsTbl.length > 0) await setInIndexedDB('Conns', connsTbl);

          if (actsRemoveTbl.length > 0) await removeFromIndexedDB('Threads', actsRemoveTbl);
          if (actsTbl.length > 0) await setInIndexedDB('Threads', actsTbl);

          if (contractsRemoveTbl.length > 0) await removeFromIndexedDB('Contracts', contractsRemoveTbl);
          if (contractsTbl.length > 0) await setInIndexedDB('Contracts', contractsTbl);

          if (taskRemoveTbl.length > 0) await removeFromIndexedDB('Tasks', taskRemoveTbl);
          if (taskTbl.length > 0) await setInIndexedDB('Tasks', taskTbl);
        }
      }
    }
  }

  // Updated updateBizLocalData method with proper error handling
  async updateBizLocalData(dataSet, isFromSCReset = false, isWithH = false, accessErrCode = 0) {
    let connData = [];
    let thrData = [];
    let taskData = [];
    let ctrcData = [];
    let idbTblPresent = false;
    const seenIDBTypes = new Set();
    if (accessErrCode === 0) {
      return;
    }

    // One-time cleanup: these used to be duplicated into localStorage; they now live only in IndexedDB.
    ['Conns', 'Threads', 'Tasks', 'Contracts'].forEach(key => localStorage.removeItem(key));

    try {
      const dataSetKeys = Object.keys(dataSet);
      const tableCount = dataSetKeys.filter(key => key.startsWith('Table')).length;

      for (let i = 1; i < tableCount; i++) {
        const tableKey = `Table${i}`;
        const tableData = dataSet[tableKey];
        if (tableData && tableData.length > 0) {
          const tblType = tableData[0].Tbl;

          if (tblType === 'MyAgencies') {
            const cleanedAgencies = tableData.map(({ Tbl, ...rest }) => rest);
            setStorageData('MyAgencies', cleanedAgencies);
            continue;
          }

          let localStorageData = localStorage.getItem(tblType) ? JSON.parse(localStorage.getItem(tblType)) : [];

          if (tblType === 'C') {
            // localStorageData holds one row per agency the user has ever logged into.
            // tableData can itself carry more than one row (e.g. Table2 holding both
            // agencies' C rows in one shot on a full reset) - write every row that's coming in.
            if (!Array.isArray(localStorageData)) localStorageData = [];
            const activeAgencyID = getAuthData()?.AgencyID;
            let existingConfig = null;

            tableData.forEach(incomingRow => {
              const incomingAgencyID = incomingRow?.AgencyID;
              const existingIndex = localStorageData.findIndex(row => Number(row?.AgencyID) === Number(incomingAgencyID));
              const mergedRow = { ...(existingIndex !== -1 ? localStorageData[existingIndex] : {}), ...incomingRow };
              delete mergedRow.Tbl;

              if (existingIndex !== -1) {
                localStorageData[existingIndex] = mergedRow;
              } else {
                localStorageData.push(mergedRow);
              }

              if (Number(incomingAgencyID) === Number(activeAgencyID)) {
                existingConfig = mergedRow;
              }
            });

            // Fall back to the last row processed if none matched the active agency (shouldn't normally happen)
            if (!existingConfig) {
              existingConfig = localStorageData[localStorageData.length - 1] || {};
            }

            // Dispatch Redux update for config changes
            setStorageData(tblType, localStorageData);
            const dispatch = this.dispatchCallback;
            if (dispatch) {

              // Handle ThreadFieldPrefs updates from server
              if (existingConfig.ThreadFieldPrefs) {
                try {
                  const threadFieldPrefsData = JSON.parse(existingConfig.ThreadFieldPrefs);
                  if (Array.isArray(threadFieldPrefsData)) {
                    // Create Redux state object with TID as keys
                    const threadFieldPrefsReduxState = {};
                    threadFieldPrefsData.forEach(item => {
                      if (item.TID && item.Prefs) {
                        // Handle case where Prefs might be stored as string
                        let prefs = item.Prefs;
                        if (typeof prefs === 'string') {
                          try {
                            prefs = JSON.parse(prefs);
                          } catch (e) {
                            console.error('Error parsing thread field prefs:', e);
                            prefs = {
                              POC: false,
                              Item: false,
                              Ref: false,
                              Qty: false,
                              AsgnTo: false,
                              NextTsk: false,
                              Since: false,
                              Source: false
                            };
                          }
                        }
                        threadFieldPrefsReduxState[item.TID] = prefs;
                      }
                    });


                    // Dispatch update to Redux with the new state structure
                    dispatch({
                      type: 'RELOAD_ALL_THREAD_FIELD_PREFS_FROM_SERVER',
                      payload: threadFieldPrefsReduxState
                    });
                  }
                } catch (error) {
                  console.error('Error parsing ThreadFieldPrefs from server:', error);
                }
              }

              // Check if connection field preferences were updated
              if (existingConfig.ConnFieldPrefs) {
                try {
                  const parsedConnPrefs = JSON.parse(existingConfig.ConnFieldPrefs);
                  const connPrefs = Array.isArray(parsedConnPrefs) && parsedConnPrefs.length > 0 ? parsedConnPrefs[0] : {};

                  // Dispatch connection field preferences update
                  dispatch({
                    type: 'RELOAD_CONN_FIELD_PREFS',
                    payload: connPrefs
                  });
                } catch (error) {
                  console.error('Error parsing contact field preferences:', error);
                }
              }

              if (existingConfig.ThreadStages) {
                try {
                  const threadStagesData = typeof existingConfig.ThreadStages === 'string'
                    ? JSON.parse(existingConfig.ThreadStages)
                    : existingConfig.ThreadStages;

                  dispatch({
                    type: 'UPDATE_THREAD_STAGES',
                    payload: threadStagesData
                  });
                } catch (error) {
                  console.error('Error parsing ThreadStages:', error);
                }
              }

              // Update general config
              dispatch({
                type: 'UPDATE_CONFIG_SUCCESS',
                payload: existingConfig
              });
            }
          } else if (!['Conns', 'Threads', 'Contracts', 'Tasks'].includes(tblType)) {
            if (tblType === 'V') {
            }
            localStorageData = await this.processTableData(tblType, tableData, localStorageData);
            if (tblType === 'V') {
            }
          }

          if (tblType === 'Conns') {
            connData = tableData;
          }
          else if (tblType === 'Threads') {
            thrData = tableData;
          }
          else if (tblType === 'Contracts') {
            ctrcData = tableData
          }
          else if (tblType === 'Tasks') {
            taskData = tableData
          }

          // Update this section in the updateBizLocalData method
          if (['Conns', 'Threads', 'Contracts', 'Tasks'].includes(tblType)) {
            idbTblPresent = true;
            seenIDBTypes.add(tblType);

            // Handle deletions first
            const deleteData = tableData.filter(item => item.IsRemove === true || item.IsRemove === 1);
            if (deleteData.length > 0) {
              await removeFromIndexedDB(tblType, deleteData);
            }

            // Handle additions and updates (exclude deletions)
            const addUpdateData = tableData.filter(item => item.IsRemove !== true && item.IsRemove !== 1);
            if ((isFromSCReset && isWithH) || accessErrCode === 1) {
              await clearIndexedDB(tblType);
              if (addUpdateData.length > 0) {
                await setInIndexedDB(tblType, addUpdateData);
              }
            }
            else if (addUpdateData.length > 0) {
              await updateIndexedDB(tblType, addUpdateData);
            }

            // Fetch the latest data from IndexedDB after all operations
            const dispatch = this.dispatchCallback;
            if (dispatch) {
              if (tblType === 'Threads') {
                const actsData = await getFromIndexedDB('Threads') || [];
                const connsData = await getFromIndexedDB('Conns') || [];
                const payload = actsData.map(act => ({
                  ...act,
                  connectionData: connsData.find(conn => conn.ConnID === act.ConnID) || null,
                }));
                dispatch({
                  type: 'FETCH_ACTION_DATA_SUCCESS',
                  payload,
                });
              } else if (tblType === 'Conns') {
                const actsData = await getFromIndexedDB('Threads') || [];
                const connsData = await getFromIndexedDB('Conns') || [];
                const payload = actsData.map(act => ({
                  ...act,
                  connectionData: connsData.find(conn => conn.ConnID === act.ConnID) || null,
                }));
                dispatch({
                  type: 'FETCH_ACTION_DATA_SUCCESS',
                  payload,
                });
                dispatch({ type: 'FETCH_CONNECTIONS_SUCCESS', payload: connsData });
              } else if (tblType === 'Contracts') {
                const contractsData = await getFromIndexedDB('Contracts') || [];
                dispatch({
                  type: 'FETCH_CONTRACTS_SUCCESS',
                  payload: contractsData,
                });
              } else if (tblType === 'Tasks') { // ✓ ADD THIS BLOCK
                const taskData = await getFromIndexedDB('Tasks') || [];
                dispatch({
                  type: 'FETCH_TASKS_SUCCESS', // or whatever your Redux action type is
                  payload: taskData,
                });
              }
            }
          } else {
            setStorageData(tblType, localStorageData);
          }

          if (tblType === 'B') {
            const dispatch = this.dispatchCallback;
            const agencyData = getAgencyData();
            if (dispatch) {
              dispatch({
                type: 'UPDATE_AGENCY_DATA_SUCCESS',
                payload: agencyData
              });
            }
          }

          if (tblType === 'D') {
            setStorageData('D', tableData);
            const dispatch = this.dispatchCallback;
            const activeData = getActiveData();
            if (dispatch) {
              dispatch({
                type: 'UPDATE_ACTIVE_DATA_SUCCESS',
                payload: activeData
              });
            }
          }

          if (tblType === 'U') {
            setUserData(tableData, accessErrCode === 1);
            const dispatch = this.dispatchCallback;
            const usersData = getUserData();
            if (dispatch) {
              dispatch({
                type: 'UPDATE_USER_DATA_SUCCESS',
                payload: usersData
              });
            }
          }

          if (tblType === 'SCRMConfigs') {
            setSCRMConfigs(tableData, accessErrCode === 1);
            const dispatch = this.dispatchCallback;
            if (dispatch) {
              dispatch({
                type: 'RELOAD_SCRM_CONFIGS'
              });
            }
          }
        }
      }

      if (isFromSCReset && isWithH) {
        const idbTypesToEnsure = ['Conns', 'Threads', 'Contracts', 'Tasks'];
        for (const missingType of idbTypesToEnsure) {
          if (!seenIDBTypes.has(missingType)) {
            await clearIndexedDB(missingType);
          }
        }

        const dispatch = this.dispatchCallback;
        if (dispatch) {
          const connsData = await getFromIndexedDB('Conns') || [];
          const actsData = await getFromIndexedDB('Threads') || [];
          const payload = actsData.map(act => ({
            ...act,
            connectionData: connsData.find(conn => conn.ConnID === act.ConnID) || null,
          }));
          dispatch({ type: 'FETCH_ACTION_DATA_SUCCESS', payload });
          dispatch({ type: 'FETCH_CONNECTIONS_SUCCESS', payload: connsData });

          const contractsData = await getFromIndexedDB('Contracts') || [];
          dispatch({ type: 'FETCH_CONTRACTS_SUCCESS', payload: contractsData });

          const refreshedTaskData = await getFromIndexedDB('Tasks') || [];
          dispatch({ type: 'FETCH_TASKS_SUCCESS', payload: refreshedTaskData });
        }
      }

      // For reset package, remove all idb and set again - but only for the tables this
      if (accessErrCode === 1) {
        const activeAgencyID = Number(getAuthData()?.AgencyID || 0);
        if (connData.length > 0) {
          await clearAgencyDataInIndexedDB('Conns', activeAgencyID);
          await setInIndexedDB('Conns', connData);
        }
        if (thrData.length > 0) {
          await clearAgencyDataInIndexedDB('Threads', activeAgencyID);
          await setInIndexedDB('Threads', thrData);
        }
        if (ctrcData.length > 0) {
          await clearAgencyDataInIndexedDB('Contracts', activeAgencyID);
          await setInIndexedDB('Contracts', ctrcData);
        }
        if (taskData.length > 0) {
          await clearAgencyDataInIndexedDB('Tasks', activeAgencyID);
          await setInIndexedDB('Tasks', taskData);
        }

        const dispatch = this.dispatchCallback;
        if (dispatch) {
          const actsData = await getFromIndexedDB('Threads') || [];
          const connsData = await getFromIndexedDB('Conns') || [];

          // Threads Redux
          const payload = actsData.map(act => ({
            ...act,
            connectionData: connsData.find(conn => conn.ConnID === act.ConnID) || null,
          }));
          dispatch({
            type: 'FETCH_ACTION_DATA_SUCCESS',
            payload,
          });

          // Conns Redux
          dispatch({
            type: 'FETCH_ACTION_DATA_SUCCESS',
            payload,
          });
          dispatch({ type: 'FETCH_CONNECTIONS_SUCCESS', payload: connsData });

          // Contracts Redux
          const contractsData = await getFromIndexedDB('Contracts') || [];
          dispatch({
            type: 'FETCH_CONTRACTS_SUCCESS',
            payload: contractsData,
          });

          // Tasks Redux
          const taskData = await getFromIndexedDB('Tasks') || [];
          dispatch({
            type: 'FETCH_TASKS_SUCCESS', // or whatever your Redux action type is
            payload: taskData,
          });
        }
      }
      return idbTblPresent;
    } catch (error) {
      console.error('Error in updateBizLocalData:', error);
      throw error;
    }
  }
  // Update accessGetData to use unauth for ModuleID 1
  async accessGetData(moduleID, actionType, data) {
    if (moduleID === 1) {
      return await this.unauthSendChange(moduleID, actionType, data, this.apiEndpoints.access);
    }

    // Authenticated logic remains
    try {
      const parameters = this.prepareAuthenticatedParameters(moduleID, actionType, data);
      const result = await this.fetchAPI(this.apiEndpoints.accessGet, JSON.stringify(parameters));

      if (result.error === true) {
        throw new Error(result.message);
      }
      return result;
    } catch (error) {
      this.handleAPIError(error, 'Get Data');
    }
  }

  // Utility method to check if string is valid JSON
  isJSON(data) {
    try {
      JSON.parse(data);
      return true;
    } catch (e) {
      return false;
    }
  }

  async accessSetData(moduleID, actionType, data) {
    // Check if this should show loading (skip for logout)
    const showLoading = true;

    showLoading && store.dispatch(setGlobalLoading(true));

    // Special handling for logout - it needs auth data in the Data field
    if (moduleID === 2 && actionType === 'logout') {
      const authData = getAuthData();
      const lastSyncDT = getLastSyncDT();
      const isWeb = this.apiCheckWeb();

      const logoutData = {
        agencyID: authData.AgencyID,
        userID: authData.UserID,
        isWeb: isWeb,
        token: isWeb ? authData.WebToken : authData.AppToken,
        lastSyncDT: lastSyncDT,
        cc: authData.CC || 91,
        mobile: authData.Mobile || ''
      };

      const parameters = [{
        ModuleID: moduleID,
        ActionType: actionType,
        Data: JSON.stringify([logoutData])
      }];

      try {
        const result = await this.fetchAPI(this.apiEndpoints.access, JSON.stringify(parameters));
        if (result.error === true) {
          throw new Error(result.message);
        }
        return result;
      } catch (error) {
        this.handleAPIError(error, 'Logout');
      } finally {
        showLoading && store.dispatch(setGlobalLoading(false));
      }
    }

    // Regular flow for other actions
    try {
      const parameters = this.prepareAuthenticatedParameters(moduleID, actionType, data);
      const result = await this.fetchAPI(this.apiEndpoints.access, JSON.stringify(parameters));

      if (result.error === true) {
        throw new Error(result.message);
      }
      if (result.data && !result.updatePackage) {
        result.updatePackage = result.data;
      }
      await this.parseResponse(result);
      return result;
    } catch (error) {
      this.handleAPIError(error, 'Set Data');
    } finally {
      showLoading && store.dispatch(setGlobalLoading(false));
    }
  }

  // Utility method to check if string is valid JSON
  isJSON(data) {
    try {
      JSON.parse(data);
      return true;
    } catch (e) {
      return false;
    }
  }
}

// Create singleton instance
const apiService = new APIService();
export default apiService;